import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useCan } from '../../lib/api/hooks';

interface TeamUser {
  userId: string;
  email: string;
  role: string;
  active: boolean;
}
interface PendingInvite {
  inviteId: string;
  email: string;
  role: string;
  expiresAt: string;
}

const ROLES = ['AccountAdmin', 'AccountManager', 'Operator', 'Viewer'];

export function Users() {
  const can = useCan();
  const qc = useQueryClient();
  const allowed = can('ManageUsers');
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<TeamUser[]>('/users'), enabled: allowed });
  const invites = useQuery({ queryKey: ['invites'], queryFn: () => api.get<PendingInvite[]>('/invites'), enabled: allowed });
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['users'] });
    qc.invalidateQueries({ queryKey: ['invites'] });
  };

  const resend = useMutation({
    mutationFn: (inviteId: string) => api.post(`/invites/${inviteId}/resend`, {}),
    onSuccess: () => { setErr(null); setNotice('Invitation re-sent.'); refresh(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not resend the invitation'),
  });
  const cancelInvite = useMutation({
    mutationFn: (inviteId: string) => api.del(`/invites/${inviteId}`),
    onSuccess: () => { setErr(null); setNotice('Invitation cancelled — its link no longer works.'); refresh(); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not cancel the invitation'),
  });

  const invite = useMutation({
    mutationFn: (v: { email: string; role: string }) => api.post<{ sent: boolean }>('/users/invite', v),
    onSuccess: (r) => {
      setErr(null);
      // `sent` comes from the API: the invite row is committed either way, so say which happened rather
      // than implying an email went out when it didn't.
      setNotice(r?.sent === false
        ? 'Invitation created, but the email could not be sent — use Resend below.'
        : 'Invitation sent.');
      refresh();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not send invite'),
  });
  const changeRole = useMutation({
    mutationFn: (v: { userId: string; role: string }) => api.patch(`/users/${v.userId}`, { role: v.role }),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not update role'),
  });
  const deactivate = useMutation({
    mutationFn: (userId: string) => api.del(`/users/${userId}`),
    onSuccess: refresh,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Could not deactivate user'),
  });

  if (!allowed) {
    return <div className="panel empty"><h3>Restricted</h3><p>Team management is available to account administrators.</p></div>;
  }

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Team &amp; users</h1>
          <div className="sub">Invite teammates and manage their roles. Roles control what each person can do in EFTCreator.</div>
        </div>
      </div>

      {err && <p className="cart-error">{err}</p>}
      {notice && !err && <div className="banner-ok" style={{ marginBottom: 12 }}>{notice}</div>}

      <InviteForm onInvite={(email, role) => invite.mutate({ email, role })} pending={invite.isPending} />

      <div className="section-title" style={{ marginTop: 22 }}>Team members</div>
      <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="tx-table">
          <thead><tr><th>Email</th><th>Role</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {users.data?.map((u) => (
              <tr key={u.userId}>
                <td>{u.email}</td>
                <td>
                  <select className="select" value={u.role} disabled={changeRole.isPending}
                    onChange={(e) => { setErr(null); changeRole.mutate({ userId: u.userId, role: e.target.value }); }}>
                    {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td><span className={`status-dot ${u.active ? 'ok' : 'muted'}`}>{u.active ? 'Active' : 'Inactive'}</span></td>
                <td style={{ textAlign: 'right' }}>
                  {u.active && (
                    <button className="btn btn-ghost btn-sm" disabled={deactivate.isPending}
                      onClick={() => { if (window.confirm(`Deactivate ${u.email}?`)) { setErr(null); deactivate.mutate(u.userId); } }}>
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(invites.data?.length ?? 0) > 0 && (
        <>
          <div className="section-title" style={{ marginTop: 22 }}>Pending invites</div>
          <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="tx-table">
              <thead><tr><th>Email</th><th>Role</th><th>Expires</th><th></th></tr></thead>
              <tbody>
                {invites.data!.map((i) => {
                  const expired = new Date(i.expiresAt).getTime() < Date.now();
                  return (
                    <tr key={i.inviteId}>
                      <td>{i.email}</td>
                      <td>{i.role}</td>
                      <td>
                        {new Date(i.expiresAt).toLocaleDateString()}
                        {expired && <div className="sub-meta warn-text">Expired — resend to issue a new link</div>}
                      </td>
                      <td className="tx-actions">
                        <button className="btn btn-ghost btn-sm" disabled={resend.isPending}
                          onClick={() => resend.mutate(i.inviteId)}>
                          {resend.isPending ? '…' : 'Resend'}
                        </button>
                        <button className="btn btn-ghost btn-sm cancel-btn" disabled={cancelInvite.isPending}
                          onClick={() => {
                            if (!window.confirm(`Cancel the invitation to ${i.email}? Their link will stop working.`)) return;
                            cancelInvite.mutate(i.inviteId);
                          }}>
                          {cancelInvite.isPending ? '…' : 'Cancel'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function InviteForm({ onInvite, pending }: { onInvite: (email: string, role: string) => void; pending: boolean }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('Operator');
  return (
    <form className="inline-form" onSubmit={(e) => { e.preventDefault(); if (email) { onInvite(email, role); setEmail(''); } }}>
      <input className="input" type="email" placeholder="teammate@example.com" value={email}
        onChange={(e) => setEmail(e.target.value)} required style={{ minWidth: 240 }} />
      <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
      </select>
      <button className="btn btn-primary" type="submit" disabled={pending || !email.trim()}>
        {pending ? 'Inviting…' : 'Invite'}
      </button>
    </form>
  );
}
